import flatpickr from "flatpickr"
import { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance"
import type { CustomLocale } from "flatpickr/dist/types/locale"
import { IS, IS_DEFINED, nil, NO, UIButton, UIComponentView, UIObject, UIView, YES } from "uicore-ts"
import { RHFocusManager } from "./Keyboard navigation/RHFocusManager"
import { RHNativeFocusItem } from "./Keyboard navigation/RHNativeFocusItem"
import { RHPageFocusManager } from "./Keyboard navigation/RHPageFocusManager"
import { RHColor } from "./RHColor"
import { formatRHDate, formatRHDateTime } from "./Utils_slop/RHDateFormatting"
import { RHFont, RHForms, RHRadius, RHShadow } from "./Utils_slop/RHDesignTokens"

// Inject Flatpickr base CSS once globally
UIView.injectCSS(`
/* Flatpickr Base Styles */
.flatpickr-calendar {
    background: transparent;
    opacity: 0;
    display: none;
    text-align: center;
    visibility: hidden;
    padding: 0;
    animation: none;
    direction: ltr;
    border: 0;
    font-size: 14px;
    line-height: 24px;
    /* border-radius: 5px; */
    border-radius: ${RHRadius.medium}px;
    position: absolute;
    width: 307.875px;
    box-sizing: border-box;
    touch-action: manipulation;
    background: #fff;
    /* box-shadow: 1px 0 0 #e6e6e6, -1px 0 0 #e6e6e6, 0 1px 0 #e6e6e6, 0 -1px 0 #e6e6e6, 0 3px 13px rgba(0,0,0,0.08); */
    box-shadow: ${RHShadow.medium};
    /* font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, ... */
    font-family: ${RHFont.familyBase};
}

.flatpickr-calendar.open,
.flatpickr-calendar.inline {
    opacity: 1;
    max-height: 640px;
    visibility: visible;
}

.flatpickr-calendar.open {
    display: inline-block;
    z-index: 99999;
}

.flatpickr-calendar.animate.open {
    animation: fpFadeInDown 300ms cubic-bezier(.23,1,.32,1);
}

.flatpickr-calendar.inline {
    display: block;
    position: relative;
    top: 2px;
}

.flatpickr-calendar.static {
    position: absolute;
    top: calc(100% + 2px);
}

.flatpickr-calendar.static.open {
    z-index: 999;
    display: block;
}

.flatpickr-calendar.hasWeeks {
    width: auto;
}

.flatpickr-calendar .hasWeeks .dayContainer,
.flatpickr-calendar .hasTime .dayContainer {
    border-bottom: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
}

.flatpickr-calendar .hasWeeks .dayContainer {
    border-left: 0;
}

.flatpickr-calendar.showTimeInput.hasTime .flatpickr-time {
    height: 40px;
    border-top: 1px solid #e6e6e6;
}

.flatpickr-calendar.noCalendar.hasTime .flatpickr-time {
    height: auto;
}

.flatpickr-calendar:before,
.flatpickr-calendar:after {
    position: absolute;
    display: block;
    pointer-events: none;
    border: solid transparent;
    content: '';
    height: 0;
    width: 0;
    left: 22px;
}

.flatpickr-calendar.rightMost:before,
.flatpickr-calendar.rightMost:after {
    left: auto;
    right: 22px;
}

.flatpickr-calendar:before {
    border-width: 5px;
    margin: 0 -5px;
}

.flatpickr-calendar:after {
    border-width: 4px;
    margin: 0 -4px;
}

.flatpickr-calendar.arrowTop:before,
.flatpickr-calendar.arrowTop:after {
    bottom: 100%;
}

.flatpickr-calendar.arrowTop:before {
    border-bottom-color: #e6e6e6;
}

.flatpickr-calendar.arrowTop:after {
    border-bottom-color: #fff;
}

.flatpickr-calendar.arrowBottom:before,
.flatpickr-calendar.arrowBottom:after {
    top: 100%;
}

.flatpickr-calendar.arrowBottom:before {
    border-top-color: #e6e6e6;
}

.flatpickr-calendar.arrowBottom:after {
    border-top-color: #fff;
}

.flatpickr-wrapper {
    position: relative;
    display: inline-block;
}

.flatpickr-months {
    display: flex;
}

.flatpickr-months .flatpickr-month {
    background: #fff;
    color: #212529;
    fill: #212529;
    height: 34px;
    line-height: 1;
    text-align: center;
    position: relative;
    user-select: none;
    overflow: hidden;
    flex: 1;
    border-bottom: 1px solid #dee2e6;
}

.flatpickr-months .flatpickr-prev-month,
.flatpickr-months .flatpickr-next-month {
    text-decoration: none;
    cursor: pointer;
    position: absolute;
    top: 0;
    height: 34px;
    padding: 10px;
    z-index: 3;
    color: #6c757d;
    fill: #6c757d;
}

.flatpickr-months .flatpickr-prev-month.flatpickr-disabled,
.flatpickr-months .flatpickr-next-month.flatpickr-disabled {
    display: none;
}

.flatpickr-months .flatpickr-prev-month i,
.flatpickr-months .flatpickr-next-month i {
    position: relative;
}

.flatpickr-months .flatpickr-prev-month.flatpickr-prev-month,
.flatpickr-months .flatpickr-next-month.flatpickr-prev-month {
    left: 0;
}

.flatpickr-months .flatpickr-prev-month.flatpickr-next-month,
.flatpickr-months .flatpickr-next-month.flatpickr-next-month {
    right: 0;
}

.flatpickr-months .flatpickr-prev-month:hover,
.flatpickr-months .flatpickr-next-month:hover {
    color: #212529;
}

.flatpickr-months .flatpickr-prev-month:hover svg,
.flatpickr-months .flatpickr-next-month:hover svg {
    fill: #212529;
}

.flatpickr-months .flatpickr-prev-month svg,
.flatpickr-months .flatpickr-next-month svg {
    width: 14px;
    height: 14px;
}

.flatpickr-months .flatpickr-prev-month svg path,
.flatpickr-months .flatpickr-next-month svg path {
    transition: fill 0.1s;
    fill: inherit;
}

.numInputWrapper {
    position: relative;
    height: auto;
}

.numInputWrapper input,
.numInputWrapper span {
    display: inline-block;
}

.numInputWrapper input {
    width: 100%;
}

.numInputWrapper input::-ms-clear {
    display: none;
}

.numInputWrapper input::-webkit-outer-spin-button,
.numInputWrapper input::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
}

.numInputWrapper span {
    position: absolute;
    right: 0;
    width: 14px;
    padding: 0 4px 0 2px;
    height: 50%;
    line-height: 50%;
    opacity: 0;
    cursor: pointer;
    border: 1px solid rgba(57,57,57,0.15);
    box-sizing: border-box;
}

.numInputWrapper span:hover {
    background: rgba(0,0,0,0.1);
}

.numInputWrapper span:active {
    background: rgba(0,0,0,0.2);
}

.numInputWrapper span:after {
    display: block;
    content: "";
    position: absolute;
}

.numInputWrapper span.arrowUp {
    top: 0;
    border-bottom: 0;
}

.numInputWrapper span.arrowUp:after {
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid rgba(57,57,57,0.6);
    top: 26%;
}

.numInputWrapper span.arrowDown {
    top: 50%;
}

.numInputWrapper span.arrowDown:after {
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid rgba(57,57,57,0.6);
    top: 40%;
}

.numInputWrapper span svg {
    width: inherit;
    height: auto;
}

.numInputWrapper span svg path {
    fill: rgba(0,0,0,0.5);
}

.numInputWrapper:hover {
    background: rgba(0,0,0,0.05);
}

.numInputWrapper:hover span {
    opacity: 1;
}

.flatpickr-current-month {
    font-size: 135%;
    line-height: inherit;
    font-weight: 600;
    color: inherit;
    position: absolute;
    width: 75%;
    left: 12.5%;
    padding: 7.48px 0 0 0;
    line-height: 1;
    height: 34px;
    display: inline-block;
    text-align: center;
    transform: translate3d(0px, 0px, 0px);
}

.flatpickr-current-month span.cur-month {
    font-family: inherit;
    font-weight: 600;
    color: inherit;
    display: inline-block;
    margin-left: 0.5ch;
    padding: 0;
}

.flatpickr-current-month span.cur-month:hover {
    background: rgba(0,0,0,0.05);
}

.flatpickr-current-month .numInputWrapper {
    width: 6ch;
    width: 7ch\0;
    display: inline-block;
}

.flatpickr-current-month .numInputWrapper span.arrowUp:after {
    border-bottom-color: #6c757d;
}

.flatpickr-current-month .numInputWrapper span.arrowDown:after {
    border-top-color: #6c757d;
}

.flatpickr-current-month input.cur-year {
    background: transparent;
    box-sizing: border-box;
    color: inherit;
    cursor: text;
    padding: 0 0 0 0.5ch;
    margin: 0;
    display: inline-block;
    font-size: inherit;
    font-family: inherit;
    font-weight: 600;
    line-height: inherit;
    height: auto;
    border: 0;
    border-radius: 0;
    vertical-align: initial;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;
}

.flatpickr-current-month input.cur-year:focus {
    outline: 0;
}

.flatpickr-current-month input.cur-year[disabled],
.flatpickr-current-month input.cur-year[disabled]:hover {
    font-size: 100%;
    color: rgba(255,255,255,0.5);
    background: transparent;
    pointer-events: none;
}

.flatpickr-current-month .flatpickr-monthDropdown-months {
    appearance: menulist;
    background: #fff;
    border: none;
    border-radius: 0;
    box-sizing: border-box;
    color: inherit;
    cursor: pointer;
    font-size: inherit;
    font-family: inherit;
    font-weight: 600;
    height: auto;
    line-height: inherit;
    margin: -1px 0 0 0;
    outline: none;
    padding: 0 0 0 0.5ch;
    position: relative;
    vertical-align: initial;
    -webkit-box-sizing: border-box;
    -webkit-appearance: menulist;
    -moz-appearance: menulist;
    width: auto;
}

.flatpickr-current-month .flatpickr-monthDropdown-months:focus,
.flatpickr-current-month .flatpickr-monthDropdown-months:active {
    outline: none;
}

.flatpickr-current-month .flatpickr-monthDropdown-months:hover {
    background: rgba(0,0,0,0.05);
}

.flatpickr-current-month .flatpickr-monthDropdown-months .flatpickr-monthDropdown-month {
    background-color: #fff;
    outline: none;
    padding: 0;
}

.flatpickr-weekdays {
    background: #fff;
    text-align: center;
    overflow: hidden;
    width: 100%;
    display: flex;
    align-items: center;
    height: 28px;
    border-bottom: 1px solid #dee2e6;
}

.flatpickr-weekdays .flatpickr-weekdaycontainer {
    display: flex;
    flex: 1;
}

span.flatpickr-weekday {
    cursor: default;
    font-size: 90%;
    background: #fff;
    color: #6c757d;
    line-height: 1;
    margin: 0;
    text-align: center;
    display: block;
    flex: 1;
    font-weight: bolder;
}

.dayContainer,
.flatpickr-weeks {
    padding: 1px 0 0 0;
}

.flatpickr-days {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: flex-start;
    width: 307.875px;
}

.flatpickr-days:focus {
    outline: 0;
}

.dayContainer {
    padding: 0;
    outline: 0;
    text-align: left;
    width: 307.875px;
    min-width: 307.875px;
    max-width: 307.875px;
    box-sizing: border-box;
    display: inline-block;
    display: -ms-flexbox;
    display: flex;
    flex-wrap: wrap;
    -ms-flex-wrap: wrap;
    -ms-flex-pack: justify;
    justify-content: space-around;
    transform: translate3d(0px, 0px, 0px);
    opacity: 1;
}

.dayContainer + .dayContainer {
    box-shadow: -1px 0 0 #e6e6e6;
}

.flatpickr-day {
    background: none;
    border: 1px solid transparent;
    border-radius: 150px;
    box-sizing: border-box;
    color: #393939;
    cursor: pointer;
    font-weight: 400;
    width: 14.2857143%;
    flex-basis: 14.2857143%;
    max-width: 39px;
    height: 39px;
    line-height: 39px;
    margin: 0;
    display: inline-block;
    position: relative;
    justify-content: center;
    text-align: center;
}

.flatpickr-day.inRange,
.flatpickr-day.prevMonthDay.inRange,
.flatpickr-day.nextMonthDay.inRange,
.flatpickr-day.today.inRange,
.flatpickr-day.prevMonthDay.today.inRange,
.flatpickr-day.nextMonthDay.today.inRange,
.flatpickr-day:hover,
.flatpickr-day.prevMonthDay:hover,
.flatpickr-day.nextMonthDay:hover,
.flatpickr-day:focus,
.flatpickr-day.prevMonthDay:focus,
.flatpickr-day.nextMonthDay:focus {
    cursor: pointer;
    outline: 0;
    background: #e6e6e6;
    border-color: #e6e6e6;
}

.flatpickr-day.today {
    border-color: #959ea9;
}

.flatpickr-day.today:hover,
.flatpickr-day.today:focus {
    border-color: #959ea9;
    background: #959ea9;
    color: #fff;
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange,
.flatpickr-day.selected.inRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.endRange.nextMonthDay {
    background: ${RHColor.primary.stringValue};
    box-shadow: none;
    color: #fff;
    border-color: ${RHColor.primary.stringValue};
}

.flatpickr-day.selected.startRange,
.flatpickr-day.startRange.startRange,
.flatpickr-day.endRange.startRange {
    border-radius: 50px 0 0 50px;
}

.flatpickr-day.selected.endRange,
.flatpickr-day.startRange.endRange,
.flatpickr-day.endRange.endRange {
    border-radius: 0 50px 50px 0;
}

.flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
.flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
.flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
    box-shadow: -10px 0 0 ${RHColor.primary.stringValue};
}

.flatpickr-day.selected.startRange.endRange,
.flatpickr-day.startRange.startRange.endRange,
.flatpickr-day.endRange.startRange.endRange {
    border-radius: 50px;
}

.flatpickr-day.inRange {
    border-radius: 0;
    box-shadow: -5px 0 0 #e6e6e6, 5px 0 0 #e6e6e6;
}

.flatpickr-day.flatpickr-disabled,
.flatpickr-day.flatpickr-disabled:hover,
.flatpickr-day.prevMonthDay,
.flatpickr-day.nextMonthDay,
.flatpickr-day.notAllowed,
.flatpickr-day.notAllowed.prevMonthDay,
.flatpickr-day.notAllowed.nextMonthDay {
    color: rgba(57,57,57,0.3);
    background: transparent;
    border-color: transparent;
    cursor: default;
}

.flatpickr-day.flatpickr-disabled,
.flatpickr-day.flatpickr-disabled:hover {
    cursor: not-allowed;
    color: rgba(57,57,57,0.1);
}

.flatpickr-day.week.selected {
    border-radius: 0;
    box-shadow: -5px 0 0 ${RHColor.primary.stringValue}, 5px 0 0 ${RHColor.primary.stringValue};
}

.flatpickr-day.hidden {
    visibility: hidden;
}

.rangeMode .flatpickr-day {
    margin-top: 1px;
}

.flatpickr-weekwrapper {
    float: left;
}

.flatpickr-weekwrapper .flatpickr-weeks {
    padding: 0 12px;
    box-shadow: 1px 0 0 #e6e6e6;
}

.flatpickr-weekwrapper .flatpickr-weekday {
    float: none;
    width: 100%;
    line-height: 28px;
}

.flatpickr-weekwrapper span.flatpickr-day,
.flatpickr-weekwrapper span.flatpickr-day:hover {
    display: block;
    width: 100%;
    max-width: none;
    color: rgba(57,57,57,0.3);
    background: transparent;
    cursor: default;
    border: none;
}

.flatpickr-innerContainer {
    display: block;
    display: flex;
    box-sizing: border-box;
    overflow: hidden;
}

.flatpickr-rContainer {
    display: inline-block;
    padding: 0;
    box-sizing: border-box;
}

.flatpickr-time {
    text-align: center;
    outline: 0;
    display: block;
    height: 0;
    line-height: 40px;
    max-height: 40px;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
}

.flatpickr-calendar.hasTime .flatpickr-time,
.flatpickr-calendar.noCalendar.hasTime .flatpickr-time {
    height: auto;
    max-height: 40px;
}

.flatpickr-time:after {
    content: "";
    display: table;
    clear: both;
}

.flatpickr-time .numInputWrapper {
    flex: 1;
    width: 40%;
    height: 40px;
    float: left;
}

.flatpickr-time .numInputWrapper span.arrowUp:after {
    border-bottom-color: #393939;
}

.flatpickr-time .numInputWrapper span.arrowDown:after {
    border-top-color: #393939;
}

.flatpickr-time.hasSeconds .numInputWrapper {
    width: 26%;
}

.flatpickr-time.time24hr .numInputWrapper {
    width: 49%;
}

.flatpickr-time input {
    background: transparent;
    box-shadow: none;
    border: 0;
    border-radius: 0;
    text-align: center;
    margin: 0;
    padding: 0;
    height: inherit;
    line-height: inherit;
    color: #393939;
    font-size: 14px;
    position: relative;
    box-sizing: border-box;
    -webkit-appearance: textfield;
    -moz-appearance: textfield;
    appearance: textfield;
}

.flatpickr-time input.flatpickr-hour {
    font-weight: bold;
}

.flatpickr-time input.flatpickr-minute,
.flatpickr-time input.flatpickr-second {
    font-weight: 400;
}

.flatpickr-time input:focus {
    outline: 0;
    border: 0;
}

.flatpickr-time .flatpickr-time-separator,
.flatpickr-time .flatpickr-am-pm {
    height: inherit;
    float: left;
    line-height: inherit;
    color: #393939;
    font-weight: bold;
    width: 2%;
    user-select: none;
    align-self: center;
}

.flatpickr-time .flatpickr-am-pm {
    outline: 0;
    width: 18%;
    cursor: pointer;
    text-align: center;
    font-weight: 400;
}

.flatpickr-time input:hover,
.flatpickr-time .flatpickr-am-pm:hover,
.flatpickr-time input:focus,
.flatpickr-time .flatpickr-am-pm:focus {
    background: #eee;
}

.flatpickr-input[readonly] {
    cursor: pointer;
}

@keyframes fpFadeInDown {
    from {
        opacity: 0;
        transform: translate3d(0, -20px, 0);
    }
    to {
        opacity: 1;
        transform: translate3d(0, 0, 0);
    }
}

.flatpickr-confirm {
    height: 40px;
    max-height: 40px;
    cursor: pointer;
    background: ${RHForms.datePickerConfirmButtonBackground};
    color: #fff;
    border: 0;
    border-top: 1px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    width: 100%;
    font-family: inherit;
}

.flatpickr-confirm:hover {
    background: ${RHForms.datePickerConfirmButtonBackgroundHover};
}

.flatpickr-confirm:active {
    background: ${RHForms.datePickerConfirmButtonBackgroundActive};
}

/* Secondary "clear" bar, shown only for optional pickers (visibility toggled per-open). */
.flatpickr-clear-button {
    display: none;
    height: 40px;
    max-height: 40px;
    cursor: pointer;
    background: #fff;
    color: #6c757d;
    border: 0;
    border-top: 1px solid #dee2e6;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    width: 100%;
    font-family: inherit;
}

.flatpickr-clear-button:hover {
    background: #f8f9fa;
    color: #212529;
}

.flatpickr-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 99998;
    display: none;
}

.flatpickr-backdrop.open {
    display: block;
}
`, "rh-datepicker-flatpickr-base")

document.addEventListener('focusin', e => console.log('focus:', e.target))

@UIComponentView
export class RHDatePicker extends UIButton {
    
    // --- Configuration ---
    
    private _type: "date" | "datetime" = "datetime"
    private _selectedDate?: Date
    private _placeholder: string = "Select date..."
    private _minDate?: Date
    private _maxDate?: Date
    private _minuteIncrement: number = 5
    
    // --- Internal Elements ---
    
    private flatpickrInstance?: FlatpickrInstance
    private hiddenInput: HTMLInputElement
    private backdropElement?: HTMLDivElement
    private valueBeforeOpen?: Date
    // The in-calendar "Tühjenda" (clear) button. Present only for optional pickers;
    // its visibility is toggled per-open in onOpen.
    private _calendarClearButtonElement?: HTMLButtonElement
    // Set to YES after programmatic close (Escape/Enter/OK) so the Focus
    // handler does not immediately re-open the calendar when focus returns
    // to the button. Cleared after the first Focus event consumes it.
    private _suppressNextFocusOpen: boolean = false
    
    // Called just before the calendar opens. Use this to update minDate, maxDate,
    // or any other configuration that should reflect the current moment.
    datePickerWillOpen?: () => void
    
    // --- Validation Configuration ---
    
    isRequired: boolean = false
    validateOnBlur: boolean = true
    protected hasBeenBlurred: boolean = false
    _hasValidated = NO
    
    // --- Constants for styling ---
    private readonly defaultBorderColor = RHForms.datePickerBorderColor.stringValue
    
    constructor(elementID?: string) {
        super(elementID, "button")
        
        // Set button type to prevent form submission
        this.viewHTMLElement.setAttribute("type", "button")
        
        // Create hidden input for flatpickr - append to document body, not button
        this.hiddenInput = UIObject.configuredWithObject(
            document.createElement("input"),
            {
                type: "text",
                style: {
                    position: "absolute",
                    opacity: "0",
                    pointerEvents: "none",
                    width: "0",
                    height: "0"
                }
            }
        )
        document.body.appendChild(this.hiddenInput);
        
        // Configure using framework API
        (this as RHDatePicker).configureWithObject({
            // backgroundColor: RHColor.textWhite,
            backgroundColor: RHForms.datePickerBackgroundColor,
            titleLabel: {
                // textColor: RHColor.textBlack,
                textColor: RHForms.datePickerTextColor,
                textAlignment: "left"
            }
        })
        
        // Inject CSS for styling
        if (this.elementID) {
            UIView.injectCSS(`
                #${this.elementID} {
                    border: 1px solid ${this.defaultBorderColor};
                    border-radius: ${RHForms.datePickerBorderRadius};
                    transition: ${RHForms.datePickerTransition};
                }
                
                #${this.elementID}_titleLabel {
                    position: static !important;
                    transform: none !important;
                    text-align: left;
                }
            `, `rh-datepicker-style-${this.elementID}`)
        }
        
        // Initialize display
        this.updateDisplayText()
        
        // Initialize flatpickr
        this.initializeFlatpickr()
        
        // Setup event listeners
        this.setupEventListeners()
        
        // Explicitly wire focused state so the focus border appears reliably
        // when the date picker receives programmatic focus from the focus manager.
        this.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            this.focused = YES
        })
        this.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            this.focused = NO
        })
    }
    
    static override controlEvent = Object.assign({}, UIButton.controlEvent, {
        DateChange: "DateChange"
    })
    
    private initializeFlatpickr() {
        // Create backdrop element
        this.backdropElement = document.createElement("div")
        this.backdropElement.className = "flatpickr-backdrop"
        this.backdropElement.addEventListener("click", () => {
            this.flatpickrInstance?.close()
        })
        document.body.appendChild(this.backdropElement)
        
        // Estonian locale
        const estonianLocale: Partial<CustomLocale> = {
            weekdays: {
                shorthand: ["P", "E", "T", "K", "N", "R", "L"],
                longhand: [
                    "Pühapäev",
                    "Esmaspäev",
                    "Teisipäev",
                    "Kolmapäev",
                    "Neljapäev",
                    "Reede",
                    "Laupäev"
                ]
            },
            months: {
                shorthand: [
                    "Jaan",
                    "Veebr",
                    "Märts",
                    "Apr",
                    "Mai",
                    "Juuni",
                    "Juuli",
                    "Aug",
                    "Sept",
                    "Okt",
                    "Nov",
                    "Dets"
                ],
                longhand: [
                    "Jaanuar",
                    "Veebruar",
                    "Märts",
                    "Aprill",
                    "Mai",
                    "Juuni",
                    "Juuli",
                    "August",
                    "September",
                    "Oktoober",
                    "November",
                    "Detsember"
                ]
            },
            firstDayOfWeek: 1,
            ordinal: () => ".",
            weekAbbreviation: "Näd",
            rangeSeparator: " kuni ",
            scrollTitle: "Keri, et suurendada",
            toggleTitle: "Klõpsa, et vahetada"
        }
        
        const config: flatpickr.Options.Options = {
            enableTime: this._type === "datetime",
            minuteIncrement: this._minuteIncrement,
            noCalendar: false,
            dateFormat: this._type === "datetime" ? "d-m-Y H:i" : "d-m-Y",
            time_24hr: true,
            allowInput: false,
            clickOpens: true, // Allow Flatpickr to handle its own click-outside behavior
            static: false, // Don't use static positioning
            locale: estonianLocale,
            // Position relative to the button
            appendTo: document.body,
            positionElement: this.viewHTMLElement,
            onReady: (selectedDates: Date[], dateStr: string, instance: FlatpickrInstance) => {
                console.log("onready " + this.elementID)
                
                if (this._type === "datetime") {
                    // Add OK button
                    const confirmButton = document.createElement("button")
                    confirmButton.className = "flatpickr-confirm"
                    confirmButton.textContent = "OK"
                    confirmButton.type = "button"
                    confirmButton.addEventListener("click", () => {
                        this._suppressNextFocusOpen = true
                        instance.close()
                        this.viewHTMLElement.focus()
                    })
                    instance.calendarContainer.appendChild(confirmButton)
                }

                // "Tühjenda" clears an optional field straight from the calendar — the
                // reported problem was that the calendar only let you pick a new date, never
                // remove one. Mirrors the OK-button pattern; visibility is set per-open in onOpen.
                const clearButton = document.createElement("button")
                clearButton.className = "flatpickr-clear-button"
                clearButton.textContent = "Tühjenda"
                clearButton.type = "button"
                clearButton.addEventListener("click", () => {
                    this._selectedDate = undefined
                    instance.clear(false)
                    this.updateDisplayText()
                    this.sendControlEventForKey(RHDatePicker.controlEvent.DateChange, nil)
                    this._suppressNextFocusOpen = true
                    instance.close()
                    this.viewHTMLElement.focus()
                })
                instance.calendarContainer.appendChild(clearButton)
                this._calendarClearButtonElement = clearButton

                // Keyboard handler wired to the calendar container.
                // Tab/Shift+Tab is handled by RHPageFocusManager via the pushed
                // _calendarFocusManager layer. We only need to handle the keys
                // that are specific to calendar interaction.
                const calendarKeydownHandler = (e: KeyboardEvent) => {
                    if (e.key === "Escape" || e.key === "Esc") {
                        if (this.valueBeforeOpen !== undefined) {
                            this._selectedDate = this.valueBeforeOpen
                            instance.setDate(this.valueBeforeOpen, false)
                        } else {
                            this._selectedDate = undefined
                            instance.clear(false)
                        }
                        this.updateDisplayText()
                        // Suppress the next Focus-triggered open so the button
                        // receives focus quietly without re-opening the calendar.
                        this._suppressNextFocusOpen = true
                        instance.close()
                        this.viewHTMLElement.focus()
                        return
                    }
                }
                
                instance.calendarContainer.addEventListener("keydown", calendarKeydownHandler)
            },
            onOpen: () => {
                console.log("onopen " + this.elementID)
                this.datePickerWillOpen?.()
                this.valueBeforeOpen = this._selectedDate ? new Date(this._selectedDate) : undefined

                if (this._type === "datetime" && !this._selectedDate && this.flatpickrInstance) {
                    const initialTime = new Date(Math.ceil(Date.now() / (5 * 60_000)) * 5 * 60_000)
                    this.flatpickrInstance.set("defaultHour", initialTime.getHours())
                    this.flatpickrInstance.set("defaultMinute", initialTime.getMinutes())
                    this.flatpickrInstance._setHoursFromDate(initialTime)
                }

                // Offer the clear button only for optional pickers that currently hold a
                // value — there is nothing to clear otherwise, and required fields never show it.
                if (this._calendarClearButtonElement) {
                    this._calendarClearButtonElement.style.display =
                        (!this.isRequired && IS_DEFINED(this._selectedDate)) ? "flex" : "none"
                }

                // The calendar is appended to document.body — OUTSIDE the scaled root — so it
                // does not inherit UIView.pageScale. Mirror the page scale onto it (anchored at
                // its top-left, where flatpickr positions it under the field) so it matches the
                // rest of the UI when the page is scaled below 1.
                const calendarContainer = this.flatpickrInstance?.calendarContainer
                if (calendarContainer) {
                    calendarContainer.style.transformOrigin = "top left"
                    calendarContainer.style.transform = "scale(" + UIView.pageScale + ")"
                }

                if (this.backdropElement) {
                    this.backdropElement.classList.add("open")
                }
                
                this.sendControlEventForKey(UIView.controlEvent.Focus, nil)
                
                // Push the calendar as an exclusive Tab layer so the page focus
                // manager cycles through calendar elements only.
                this._calendarFocusManager = this._buildCalendarFocusManager()
                RHPageFocusManager.shared.pushManagerAsNewLayer(this._calendarFocusManager)
                
                // Move focus into the calendar immediately so the user can
                // navigate with arrow keys and Tab right away. Deferred one
                // frame so flatpickr has finished positioning the calendar.
                requestAnimationFrame(() => {
                    if (this.flatpickrInstance?.isOpen) {
                        this._calendarFocusManager?.focusFirst()
                    }
                })
            },
            onChange: (selectedDates: Date[]) => {
                console.log("onchange " + this.elementID)
                if (selectedDates.length > 0) {
                    this._selectedDate = selectedDates[0]
                    this.updateDisplayText()
                    this.sendControlEventForKey(RHDatePicker.controlEvent.DateChange, nil)
                    
                    if (this.validateOnBlur) {
                        this.validate()
                    }
                }
                
                // Close after selection for date-only picker and return focus
                // to the button without re-opening.
                if (this._type === "date" && selectedDates.length > 0) {
                    this._suppressNextFocusOpen = true
                    this.flatpickrInstance?.close()
                    this.viewHTMLElement.focus()
                }
            },
            onClose: () => {
                console.log("onclose " + this.elementID)
                this.hasBeenBlurred = YES
                this.hiddenInput.blur()
                
                // Pop the calendar focus layer so Tab navigation returns to
                // the rest of the page.
                if (this._calendarFocusManager) {
                    RHPageFocusManager.shared.popLayer()
                    this._calendarFocusManager = undefined
                }
                
                // Hide backdrop
                if (this.backdropElement) {
                    this.backdropElement.classList.remove("open")
                }
                
                if (this.validateOnBlur) {
                    this.validate()
                }
            }
        }
        
        if (IS_DEFINED(this._minDate)) {
            config.minDate = this._minDate
        }
        
        if (IS_DEFINED(this._maxDate)) {
            config.maxDate = this._maxDate
        }
        
        this.flatpickrInstance = flatpickr(this.hiddenInput, config)
    }
    
    private setupEventListeners() {
        // Toggle calendar on click or keyboard activation
        this.addTargetForControlEvents([
            UIView.controlEvent.PointerUpInside,
            UIView.controlEvent.EnterDown
        ], () => {
            if (this.enabled) {
                if (this.flatpickrInstance?.isOpen) {
                    this.flatpickrInstance.close()
                } else {
                    this._openCalendar()
                }
            }
        })
        
        // Open calendar when the button receives focus via keyboard (Tab navigation).
        // Pointer-driven focus is handled by PointerUpInside instead — we skip it
        // here to avoid opening twice.
        // When _suppressNextFocusOpen is set (after Escape/Enter/OK closed the
        // calendar), we skip opening once and clear the flag.
        this.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            if (this._suppressNextFocusOpen) {
                this._suppressNextFocusOpen = false
                return
            }
            if (this.enabled && !this.flatpickrInstance?.isOpen && !this._isPointerInside) {
                this._openCalendar()
            }
        })
    }
    
    private _openCalendar() {
        // Flatpickr monitors focus on the input it was initialised with.
        // If that input is not focused when open() is called, flatpickr
        // immediately fires onClose because it detects an outside-focus event.
        // Focusing the hidden input first prevents that spurious close.
        this.hiddenInput.focus()
        this.flatpickrInstance?.open()
    }
    
    // The focus manager pushed as an exclusive layer while the calendar is open.
    // Cleared when the calendar closes.
    private _calendarFocusManager?: RHFocusManager
    
    // Builds an RHFocusManager whose focusableItems are the live DOM elements
    // inside the flatpickr calendar container, wrapped as RHNativeFocusItem
    // instances. Called fresh each time the calendar opens so the correct
    // elements are captured.
    //
    // The day grid uses the container element (.flatpickr-days) for containment
    // matching so that Tab is correctly detected as "inside the grid" regardless
    // of which individual day cell is focused. The focusTarget callback then
    // lands focus on the selected/today/first-available cell inside it.
    private _buildCalendarFocusManager(): RHFocusManager {
        return new RHFocusManager(() => {
            const instance = this.flatpickrInstance
            if (!instance) { return [] }
            
            const container = instance.calendarContainer
            const items: RHNativeFocusItem[] = []
            
            // 1. Day grid — Tab stop covers the whole container so any focused
            //    day cell satisfies containment. Arrow keys move between cells
            //    natively; Tab moves to the next logical stop.
            const daysContainer = container.querySelector<HTMLElement>(".flatpickr-days")
            if (daysContainer) {
                items.push(new RHNativeFocusItem(daysContainer, () =>
                    container.querySelector<HTMLElement>(".flatpickr-day.selected") ??
                    container.querySelector<HTMLElement>(".flatpickr-day.today") ??
                    container.querySelector<HTMLElement>(".flatpickr-day:not(.flatpickr-disabled):not(.hidden)")
                ))
            }
            
            // 2. Hours input (datetime only)
            const hoursInput = container.querySelector<HTMLElement>("input.flatpickr-hour")
            if (hoursInput) { items.push(new RHNativeFocusItem(hoursInput)) }
            
            // 3. Minutes input (datetime only)
            const minutesInput = container.querySelector<HTMLElement>("input.flatpickr-minute")
            if (minutesInput) { items.push(new RHNativeFocusItem(minutesInput)) }
            
            // 4. Month dropdown
            const monthDropdown = container.querySelector<HTMLElement>("select.flatpickr-monthDropdown-months")
            if (monthDropdown) { items.push(new RHNativeFocusItem(monthDropdown)) }
            
            // 5. Year input
            const yearInput = container.querySelector<HTMLElement>("input.cur-year")
            if (yearInput) { items.push(new RHNativeFocusItem(yearInput)) }
            
            // 6. OK / confirm button (datetime only)
            const confirmButton = container.querySelector<HTMLElement>("button.flatpickr-confirm")
            if (confirmButton) { items.push(new RHNativeFocusItem(confirmButton)) }

            // 7. "Tühjenda" clear button (optional pickers only; skip when hidden).
            const clearButton = container.querySelector<HTMLElement>("button.flatpickr-clear-button")
            if (clearButton && clearButton.style.display !== "none") {
                items.push(new RHNativeFocusItem(clearButton))
            }

            return items
        })
    }
    
    private updateDisplayText() {
        if (this._selectedDate) {
            this.titleLabel.text = this._type === "datetime"
                                   ? formatRHDateTime(this._selectedDate)
                                   : formatRHDate(this._selectedDate)
            this.titleLabel.textColor = RHForms.datePickerTextColor
        }
        else {
            this.titleLabel.text = this._placeholder
            this.titleLabel.textColor = RHForms.datePickerPlaceholderTextColor
        }
    }
    
    // --- Public API ---
    
    get type(): "date" | "datetime" {
        return this._type
    }
    
    set type(value: "date" | "datetime") {
        this._type = value
        
        if (this.flatpickrInstance) {
            this.flatpickrInstance.destroy()
            this.initializeFlatpickr()
        }
        
        this.updateDisplayText()
    }
    
    get selectedDate(): Date | undefined {
        return this._selectedDate
    }
    
    set selectedDate(date: Date | undefined) {
        this._selectedDate = date
        
        if (this.flatpickrInstance) {
            if (date) {
                this.flatpickrInstance.setDate(date, false)
            }
            else {
                this.flatpickrInstance.clear()
            }
        }
        
        this.updateDisplayText()
        this._resetValidationState()
    }
    
    get placeholder(): string {
        return this._placeholder
    }
    
    set placeholder(value: string) {
        this._placeholder = value
        this.updateDisplayText()
    }
    
    get minDate(): Date | undefined {
        return this._minDate
    }
    
    set minDate(date: Date | undefined) {
        this._minDate = date
        if (this.flatpickrInstance) {
            this.flatpickrInstance.set("minDate", date)
        }
    }
    
    get maxDate(): Date | undefined {
        return this._maxDate
    }
    
    set maxDate(date: Date | undefined) {
        this._maxDate = date
        if (this.flatpickrInstance) {
            this.flatpickrInstance.set("maxDate", date)
        }
    }

    get minuteIncrement(): number {
        return this._minuteIncrement
    }

    set minuteIncrement(minuteIncrement: number) {
        this._minuteIncrement = minuteIncrement
        if (this.flatpickrInstance) {
            this.flatpickrInstance.set("minuteIncrement", minuteIncrement)
        }
    }

    clear() {
        this._selectedDate = undefined
        try {
            this.flatpickrInstance?.clear()
        }
        catch (error) {
            // Nothing
        }
        this.updateDisplayText()
        this._resetValidationState()
    }
    
    // --- Validation ---
    
    validate(): boolean {
        this._hasValidated = YES
        const hasValue = this._selectedDate !== undefined
        let isValid = YES
        
        const hasValidationRules = this.isRequired
        
        if (!hasValidationRules) {
            this.resetValidationVisuals()
            return YES
        }
        
        if (this.isRequired && !hasValue) {
            isValid = NO
        }
        if (IS(this.minDate) && IS(this._selectedDate) && this.minDate.getTime() > this._selectedDate.getTime()) {
            isValid = NO
        }
        if (IS(this.maxDate) && IS(this._selectedDate) && this.maxDate.getTime() < this._selectedDate.getTime()) {
            isValid = NO
        }
        
        this.isValid = isValid
        return isValid
    }
    
    resetValidationVisuals() {
        this.style.borderColor = this.defaultBorderColor
        this.style.boxShadow = "none"
    }


    _resetValidationState() {
        this._hasValidated = NO
        this.hasBeenBlurred = NO
        this.resetValidationVisuals()
    }
    
    set isValid(value: boolean) {
        if (value) {
            this.style.borderColor = RHColor.success.stringValue
            this.style.boxShadow = "none"
        }
        else {
            this.style.borderColor = RHColor.danger.stringValue
            this.style.boxShadow = "none"
        }
    }
    
    // --- State Updates ---
    
    override updateContentForFocusedState() {
        this.style.borderColor = RHColor.primaryBorderHover.stringValue
        this.style.boxShadow = `0 0 0 0.25rem ${RHColor.primaryShadow.stringValue}`
        this.style.outline = "0"
    }
    
    override updateContentForNormalState() {
        if (!this.focused) {
            this.resetValidationVisuals()
            if (this._hasValidated || this.hasBeenBlurred) {
                this.validate()
            }
        }
    }
    
    override updateContentForCurrentEnabledState() {
        if (this.enabled) {
            this.alpha = 1
            if (this._selectedDate) {
                this.titleLabel.textColor = RHForms.datePickerTextColor
            }
            else {
                this.titleLabel.textColor = RHForms.datePickerPlaceholderTextColor
            }
            this.backgroundColor = RHForms.datePickerBackgroundColor
            this.style.borderColor = this.defaultBorderColor

            if (this._hasValidated || this.hasBeenBlurred) {
                this.validate()
            }
            else {
                this.resetValidationVisuals()
            }
        }
        else {
            this.alpha = 1
            this.titleLabel.textColor = RHForms.datePickerDisabledTextColor
            this.backgroundColor = RHForms.datePickerDisabledBackgroundColor
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            
            this.flatpickrInstance?.close()
        }
        
        this.userInteractionEnabled = this.enabled
    }
    
    // --- Lifecycle ---
    
    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        
        // Re-append hiddenInput and re-initialise flatpickr if it was destroyed
        // when the view was previously removed from the tree.
        if (!this.flatpickrInstance) {
            document.body.appendChild(this.hiddenInput)
            this.initializeFlatpickr()
        }
    }
    
    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        
        // If the picker is destroyed while the calendar is still open, pop the
        // focus layer so the page focus manager is not left in a broken state.
        if (this._calendarFocusManager) {
            RHPageFocusManager.shared.popLayer()
            this._calendarFocusManager = undefined
        }
        
        this.flatpickrInstance?.destroy()
        this.flatpickrInstance = undefined
        
        // Remove the hidden input from DOM
        if (this.hiddenInput.parentNode) {
            this.hiddenInput.parentNode.removeChild(this.hiddenInput)
        }
        
        // Remove the backdrop from DOM
        if (this.backdropElement && this.backdropElement.parentNode) {
            this.backdropElement.parentNode.removeChild(this.backdropElement)
        }
        this.backdropElement = undefined
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const rect = this.viewHTMLElement.getBoundingClientRect()
        this.hiddenInput.style.position = "fixed"
        this.hiddenInput.style.top = rect.top + "px"
        this.hiddenInput.style.left = rect.left + "px"
        this.hiddenInput.style.width = rect.width + "px"
        this.hiddenInput.style.height = rect.height + "px"
        this.hiddenInput.style.opacity = "0"
        this.hiddenInput.style.pointerEvents = "none"
    }
    
}
